/**
 * "Best match for my CV first" order for the SRP (TEC-711). The visitor's CV skills (each with its
 * cross-language variants, see utils/cv-check/skill-synonyms.ts) become a Typesense `_eval` ladder:
 * jobs listing three or more of the CV's top skills first, then two, then any one, then the rest.
 * Nothing is filtered out; only the order changes. Within a band, the newest job comes first.
 *
 * `_eval([ (filter):weight, ... ])` scores a document by the FIRST expression it matches, so "at
 * least N of these skills" is written out as the OR of every N-skill combination. That grows fast,
 * so each band uses only as many of the CV's first skills (the ones the person listed first) as fit
 * Typesense's term budget, and the "any one" band (a single term) a few more.
 */

/**
 * Typesense rejects an `_eval` expression with more than 50 filter terms ("Error parsing eval
 * expression in sort_by clause", verified live 2026-10-14; a `[a,b,c]` value list counts as one
 * term, and the limit is per expression, not per sort_by). The 3-skill band over 6 skills is
 * 20 triples = 60 terms, which failed the whole search for every CV with 6+ skills.
 */
const MAX_TERMS_PER_EXPRESSION = 50;
const MAX_BAND_SKILLS = 8;
const ANY_SKILLS = 12;
const VARIANTS_PER_SKILL = 4;

/** Backticks end a Typesense literal; `word:` reads as a field name to the proxy's field check. */
function literal(value: string): string {
  return `\`${value.replace(/[`:]/g, ' ').replace(/\s+/g, ' ').trim()}\``;
}

function skillClause(variants: readonly string[]): string {
  return `skillsAndTechnologies:[${variants.map(literal).join(',')}]`;
}

function binomial(n: number, k: number): number {
  let out = 1;
  for (let i = 0; i < k; i++) out = (out * (n - i)) / (i + 1);
  return out;
}

/** The most leading skills whose every `size`-combination fits one expression's term budget. */
function bandSkillCount(available: number, size: number): number {
  let n = Math.min(available, MAX_BAND_SKILLS);
  while (n > size && binomial(n, size) * size > MAX_TERMS_PER_EXPRESSION) n--;
  return n;
}

function combinations<T>(items: readonly T[], size: number): T[][] {
  if (size === 0) return [[]];
  const out: T[][] = [];
  items.forEach((item, i) => {
    for (const rest of combinations(items.slice(i + 1), size - 1)) out.push([item, ...rest]);
  });
  return out;
}

/**
 * The sort_by for CV-match order, or undefined when there are no usable skills. `cvSkills` holds one
 * entry per CV skill, each the list of names that skill goes by (the CV's own wording first).
 */
export function buildCvMatchSortBy(cvSkills: readonly (readonly string[])[]): string | undefined {
  const skills = cvSkills
    .map((variants) =>
      Array.from(new Set(variants.map((v) => v.replace(/[`:]/g, ' ').trim()).filter((v) => v.length >= 2))).slice(0, VARIANTS_PER_SKILL)
    )
    .filter((variants) => variants.length > 0);
  if (skills.length === 0) return undefined;

  const clauses = skills.map(skillClause);
  const ladder: string[] = [];
  for (const size of [3, 2]) {
    if (clauses.length < size) continue;
    const band = clauses.slice(0, bandSkillCount(clauses.length, size));
    const anyCombo = combinations(band, size)
      .map((combo) => `(${combo.join(' && ')})`)
      .join(' || ');
    ladder.push(`(${anyCombo}):${size}`);
  }
  const anyOne = skillClause(skills.slice(0, ANY_SKILLS).flat());
  ladder.push(`(${anyOne}):1`);
  // No _text_match key: the SRP proxy only accepts schema field names in sort_by, and a typed
  // query already narrows the results to relevant jobs.
  return `_eval([ ${ladder.join(', ')} ]):desc,postedDate:desc`;
}
